import type { SelectionMergeMode } from '@frontend/viewer-core';

/** The modifier state a merge mode is read from — a real event, or a stub. */
export type SelectionModifiers = {
  readonly shiftKey: boolean;
};

/**
 * Merge modes a panel row select handler can be given — never `'toggle'`.
 *
 * Threaded through `LayerManager.select`, `MappingManager.selectRole`, and
 * every prop and callback between a row and them, so a future caller cannot
 * pass `'toggle'` in and reproduce the half-selected-row bug `panelRowMergeMode`
 * exists to avoid.
 */
export type PanelSelectionMode = Exclude<SelectionMergeMode, 'toggle'>;

/**
 * What Shift means when a gesture names individual items: the canvas click and
 * the marquee release.
 *
 * Toggle, not add — the spec is explicit that Shift over something already
 * selected takes it out, which is what every multi-select surface a user brings
 * habits from does (Figma, Finder, Explorer). It is also why there is no
 * subtract modifier: Shift already covers both directions, which freed Alt to
 * mean something else entirely.
 */
export const pickMergeMode = ({
  shiftKey,
}: SelectionModifiers): SelectionMergeMode => (shiftKey ? 'toggle' : 'replace');

/**
 * What Shift means when a gesture names one *aggregate* — a layer row, a mapping
 * row, and whatever panel comes next.
 *
 * Resolved against the row's own selected state rather than per ref, because the
 * refs behind a row are an implementation detail the user never sees. A row is
 * one thing: Shift-clicking an unselected row adds all of it, and Shift-clicking
 * a selected row removes all of it.
 *
 * Toggling ref by ref would give the same answer for a row that is entirely in
 * or entirely out of the selection, and a nonsense one in between — a
 * half-selected layer would have its selected part removed and its unselected
 * part added, scrambling the selection rather than completing it. Adding is the
 * useful reading there, and it falls out of `isSelected` being all-or-nothing
 * (see `selectedLayers` / `selectedRoles`).
 */
export const panelRowMergeMode = (
  { shiftKey }: SelectionModifiers,
  isSelected: boolean,
): PanelSelectionMode => {
  if (!shiftKey) {
    return 'replace';
  }
  return isSelected ? 'subtract' : 'add';
};
